import { mdsColors } from "@/design-system/foundations";
import { MdsTextSize } from "@/design-system/components/text";
import { MdsText } from "@/design-system/components/text";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import styled from "@emotion/styled";
import { MutableRefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import { MdsSpinner } from "@/design-system/components/loader/MdsSpinner";
import { LoadingContainer } from "@/components/note/editor/source-bar/floating-preview-content.styles";
import { components } from "@/__openapi__/backend/schema";
import { css } from "@/domains/emotion";
import { motion } from "motion/react";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { resolveCapturedAudioSessionSyncModelUuid } from "@/modules/uuid/sync-models/resolveCapturedAudioSessionSyncModelUuid";
import { MdsButton, MdsButtonVariant } from "@/design-system/components/button";
import { transformNoteWithVoice } from "@/actions/transformNoteWithVoice";
import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import { toastModule } from "@/modules/toast";
import useDebouncedCallback from "@/domains/react/useDebouncedCallback";

type NoteSourceAudioArtifactData = components["schemas"]["AudioArtifactInternalResponseSchema"];

type TProps = {
  isOpen: boolean;
  isLoading: boolean;
  artifactData: NoteSourceAudioArtifactData | null;
  playingSourceIdRef: MutableRefObject<string | null>;
  sourceId: string;
  noteId: string;
  noteTitle: string;
};

export const FloatingAudioPreviewContent = observer(function FloatingAudioPreviewContent({
  isLoading,
  artifactData,
  isOpen,
  playingSourceIdRef,
  sourceId,
  noteId,
  noteTitle,
}: TProps) {
  const { store } = useAppStore();
  const [isTransforming, setIsTransforming] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const duration = artifactData?.audio_duration_seconds ?? 0;
  const animationFrameRef = useRef<number>();
  const lastUpdateTimeRef = useRef<number>(0);
  const hasTrackedScrollRef = useRef(false);

  const SKIP_SECONDS = 5;

  useEffect(() => {
    if (!isOpen) hasTrackedScrollRef.current = false;
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && artifactData) {
      const sessionId = resolveCapturedAudioSessionSyncModelUuid({
        capturedAudioId: artifactData.captured_audio_id,
      });
      trackEvent(TrackedEvent.VoiceRecordingView, {
        note_id: noteId,
        captured_audio_id: artifactData.captured_audio_id,
        session_id: sessionId,
        note_title: noteTitle,
      });
    }
  }, [isOpen, artifactData, noteId, sourceId, noteTitle]);

  const updateProgress = (timestamp: number) => {
    if (!audioRef.current || !isPlaying) return;

    if (!lastUpdateTimeRef.current) {
      lastUpdateTimeRef.current = timestamp;
    }

    const elapsed = timestamp - lastUpdateTimeRef.current;
    if (elapsed > 16) {
      // Update roughly every 16ms (60fps)
      setCurrentTime(audioRef.current.currentTime);
      lastUpdateTimeRef.current = timestamp;
    }

    animationFrameRef.current = requestAnimationFrame(updateProgress);
  };

  useEffect(() => {
    if (isPlaying) {
      animationFrameRef.current = requestAnimationFrame(updateProgress);
    } else {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    }

    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying]);

  useEffect(() => {
    if (isPlaying || isTransforming) playingSourceIdRef.current = sourceId;
    else playingSourceIdRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTransforming, isPlaying]);

  useLayoutEffect(() => {
    if (isOpen && audioRef.current && artifactData?.download_url) {
      const currentAudioRef = audioRef.current;

      currentAudioRef.load();
      currentAudioRef.addEventListener("ended", handleEnded);

      return () => {
        currentAudioRef.removeEventListener("ended", handleEnded);
      };
    }
  }, [isOpen, artifactData?.download_url]);

  const handlePlayPause = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();

        if (artifactData) {
          const sessionId = resolveCapturedAudioSessionSyncModelUuid({
            capturedAudioId: artifactData.captured_audio_id,
          });
          trackEvent(TrackedEvent.VoiceRecordingAudioPlay, {
            note_id: noteId,
            captured_audio_id: artifactData.captured_audio_id,
            session_id: sessionId,
            note_title: noteTitle,
          });
        }
      }
      setIsPlaying(!isPlaying);
    }
  };

  const progressBarRef = useRef<HTMLDivElement>(null);
  const scrubbingLastCurrentTimeRef = useRef(-1);

  const getNewTime = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!progressBarRef.current) return 0;

    const newTime = (event.nativeEvent.offsetX / progressBarRef.current.clientWidth) * duration;
    return Math.max(0, Math.min(newTime, duration));
  };

  const handleProgressBarMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!audioRef.current || !progressBarRef.current) return;

    const newTime = getNewTime(event);
    scrubbingLastCurrentTimeRef.current = newTime;
    audioRef.current.currentTime = newTime;
    setCurrentTime(newTime);

    return true;
  };

  const handleProgressBarMouseMove = useDebouncedCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!audioRef.current || !progressBarRef.current || event.buttons !== 1) return;

      const newTime = getNewTime(event);
      const diff = Math.abs(newTime - scrubbingLastCurrentTimeRef.current);
      if (diff < 0.05) return true;

      scrubbingLastCurrentTimeRef.current = newTime;
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);

      return true;
    },
    10,
    { trailing: true, leading: false }
  );

  const handleProgressBarMouseUp = () => {
    try {
      handleProgressBarMouseMove.flush();

      return true;
    } finally {
      scrubbingLastCurrentTimeRef.current = -1;
    }
  };

  const handleScroll = () => {
    if (!hasTrackedScrollRef.current && artifactData) {
      const sessionId = resolveCapturedAudioSessionSyncModelUuid({
        capturedAudioId: artifactData.captured_audio_id,
      });
      trackEvent(TrackedEvent.VoiceRecordingTranscriptScroll, {
        note_id: noteId,
        captured_audio_id: artifactData.captured_audio_id,
        session_id: sessionId,
        note_title: noteTitle,
      });
      hasTrackedScrollRef.current = true;
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
  };

  const handleSkip = (direction: "back" | "forward") => {
    if (audioRef.current) {
      let newTime =
        audioRef.current.currentTime + (direction === "back" ? -SKIP_SECONDS : SKIP_SECONDS);
      newTime = Math.max(0, Math.min(newTime, duration));
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleIncorporateIntoNote = async () => {
    if (!artifactData?.captured_audio_id) return;
    setIsTransforming(true);
    try {
      const { error } = await transformNoteWithVoice({
        noteId,
        capturedAudioId: artifactData.captured_audio_id,
        fromSource: true,
        store,
      });
      if (error) {
        toastModule.triggerToast({ content: error });
      }
    } finally {
      setIsTransforming(false);
    }
  };

  const formatTime = (time: number) => {
    if (!isFinite(time)) return "00:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  };

  return (
    <Card>
      {isLoading ? (
        <LoadingContainer>
          <MdsSpinner size={24} />
        </LoadingContainer>
      ) : (
        <AudioPlayerContainer>
          <AudioControlsContainer>
            <StyledProgressBar
              ref={progressBarRef}
              onMouseDown={handleProgressBarMouseDown}
              onMouseUp={handleProgressBarMouseUp}
              onMouseMove={handleProgressBarMouseMove}
            >
              <ProgressFill style={{ width: `${(currentTime / (duration || 1)) * 100}%` }} />
            </StyledProgressBar>
            <ControlsRow>
              <TimeText>{formatTime(currentTime)}</TimeText>
              <ControlButtons>
                <ControlButton onClick={() => handleSkip("back")}>
                  <MdsIcon
                    kind={MdsIconKind.SkipBackward}
                    innerStyles={{ Icon: { className: skipButtonStyles } }}
                  />
                </ControlButton>
                <PlayPauseButton onClick={handlePlayPause}>
                  <MdsIcon
                    kind={isPlaying ? MdsIconKind.PauseSolid : MdsIconKind.PlaySolid}
                    innerStyles={{ Icon: { className: playPauseButtonStyles } }}
                  />
                </PlayPauseButton>
                <ControlButton onClick={() => handleSkip("forward")}>
                  <MdsIcon
                    kind={MdsIconKind.SkipForward}
                    innerStyles={{ Icon: { className: skipButtonStyles } }}
                  />
                </ControlButton>
              </ControlButtons>
              <TimeTextRight>{formatTime(duration)}</TimeTextRight>
            </ControlsRow>
          </AudioControlsContainer>
          <audio
            ref={audioRef}
            src={artifactData?.download_url}
            onEnded={handleEnded}
            style={{ display: "none" }}
          />
        </AudioPlayerContainer>
      )}
      {artifactData?.transcript_text && (
        <TranscriptContainer onScroll={handleScroll}>
          <TranscriptText size={MdsTextSize.Medium} color={mdsColors().grey.x700}>
            {artifactData.transcript_text}
          </TranscriptText>
        </TranscriptContainer>
      )}
      <ButtonContainer>
        <MdsButton
          iconKind={MdsIconKind.Sparkle}
          variant={MdsButtonVariant.FilledDark}
          label="Incorporate into note"
          isLoading={isTransforming}
          onClick={handleIncorporateIntoNote}
          isDisabled={isTransforming}
        />
      </ButtonContainer>
    </Card>
  );
});

const Card = styled.div`
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 2px 12px 0 rgba(16, 24, 40, 0.08);
  padding: 0;
  width: min(600px, 95vw);
  max-width: 95vw;
  display: flex;
  flex-direction: column;
`;

const AudioPlayerContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: ${({ theme }) => theme.spacing.md};
  background-color: ${({ theme }) => theme.colors.grey.x25};
`;

const AudioControlsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const StyledProgressBar = styled.div`
  width: 100%;
  height: 14px;
  background: ${({ theme }) => theme.colors.grey.x200};
  border-radius: 7px;
  overflow: hidden;
  position: relative;
  user-select: none;
  cursor: pointer;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: ${({ theme }) => theme.colors.grey.x600};
  border-radius: 7px;
  will-change: width;
  user-select: none;
  cursor: pointer;
`;

const TimeText = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.small};
  color: ${({ theme }) => theme.colors.grey.x600};
  font-weight: ${({ theme }) => theme.fontWeights.semiBold};
  width: 60px;
`;

const TimeTextRight = styled(TimeText)`
  text-align: right;
`;

const ControlsRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const ControlButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.grey.x500};
  transition: background 0.2s;
  &:hover {
    background: ${({ theme }) => theme.colors.grey.x100};
  }
`;

const PlayPauseButton = styled(ControlButton)`
  background: ${({ theme }) => theme.colors.grey.x900};
  color: #fff;
  &:hover {
    background: ${({ theme }) => theme.colors.grey.x700};
  }
`;

const TranscriptContainer = styled.div`
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};
  max-height: 120px;
  overflow-y: auto;
`;

const TranscriptText = styled(MdsText)`
  white-space: pre-wrap;
  font-size: 15px;
  line-height: 22px;
`;

const ControlButtons = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const skipButtonStyles = css({
  fontSize: "24px",
  width: "24px",
  height: "24px",
});

const playPauseButtonStyles = css({
  fontSize: "24px",
  width: "24px",
  height: "24px",
  color: mdsColors().white,
});

const ButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: ${({ theme }) => `${theme.spacing.md}`};
`;
